import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine, faFileAlt, faMoneyBillWave, faBuilding,
  faPercent, faCoins, faHandHoldingUsd, faChartPie, faUsers, faRotate,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../../components/shared/DataTable';
import { fmtMoney } from '../../utils/formatMoney';
import {
  UiPageHeader,
  UiFilterPanel,
  UiStatsGrid,
} from '../../components/ui';
import type { StatCardDef } from '../../components/ui';
import P2pReportPanel, { P2pReportSummary } from '../../components/P2pReportPanel';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);
const TZ = 'Asia/Bangkok';

const todayBKK = () => dayjs().tz(TZ).format('YYYY-MM-DD');

interface MerchantReportRow {
  merchant_name: string;
  merchant_email: string;
  total_orders: number;
  paid_orders: number;
  pending_orders: number;
  expired_orders: number;
  base_revenue: number;
  total_revenue: number;
  cent_difference: number;
  total_fee: number;
  total_payout_fee: number;
  net_revenue: number;
  success_rate: number;
}

interface ReportSummary {
  total_orders: number;
  base_revenue?: number;
  total_revenue: number;
  cent_difference?: number;
  total_fee?: number;
  net_revenue?: number;
  success_rate: number;
  total_payouts?: number;
  total_payout_amount?: number;
  total_payout_fee?: number;
}

interface ReportsData {
  merchants: MerchantReportRow[];
  summary: ReportSummary;
}

const fmtAmount = (val: number) =>
  parseFloat(String(val ?? 0)).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SystemReportsPage: React.FC = () => {
  const [dateFrom, setDateFrom] = useState(todayBKK);
  const [dateTo, setDateTo] = useState(todayBKK);
  const [appliedDateFrom, setAppliedDateFrom] = useState(todayBKK);
  const [appliedDateTo, setAppliedDateTo] = useState(todayBKK);
  const [reports, setReports] = useState<ReportsData>({
    merchants: [],
    summary: { total_orders: 0, total_revenue: 0, success_rate: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [p2p, setP2p] = useState<P2pReportSummary | null>(null);

  const loadReports = useCallback(async (from = appliedDateFrom, to = appliedDateTo) => {
    setLoading(true);
    try {
      const response = await merchantApi.get(`/admin/reports?start_date=${from}&end_date=${to}`);
      setReports(response.data.data);
      // 💧 P2P (แยก) ตามช่วงวันที่เลือก
      const pFrom = dayjs(from).startOf('day').toISOString();
      const pTo = dayjs(to).endOf('day').toISOString();
      const p2pRes = await merchantApi.get('/admin/p2p-report', { params: { date_from: pFrom, date_to: pTo } }).catch(() => null);
      setP2p(p2pRes?.data?.success ? (p2pRes.data.data.summary ?? null) : null);
    } catch (error: any) {
      toast.error('ไม่สามารถโหลดรายงานได้: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  }, [appliedDateFrom, appliedDateTo]);

  useEffect(() => {
    loadReports();
  }, [appliedDateFrom, appliedDateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => {
    if (dayjs(dateFrom).isAfter(dayjs(dateTo), 'day')) {
      toast.error('วันที่เริ่มต้นต้องน้อยกว่าหรือเท่ากับวันที่สิ้นสุด');
      return;
    }
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
  };

  const handleReset = () => {
    const today = todayBKK();
    setDateFrom(today);
    setDateTo(today);
    setAppliedDateFrom(today);
    setAppliedDateTo(today);
  };

  const s = reports.summary;

  const statCards: StatCardDef[] = useMemo(() => [
    {
      icon: faFileAlt,
      iconClassName: 'bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400',
      label: 'Total Orders',
      value: <span className="text-gray-800 dark:text-slate-100">{s.total_orders.toLocaleString()}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">ทุก Merchants</span>,
    },
    {
      icon: faMoneyBillWave,
      iconClassName: 'bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400',
      label: 'ยอดฝาก (Base)',
      value: <span className="text-violet-600 dark:text-violet-400">฿{fmtAmount(s.base_revenue || 0)}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">ยอดที่ลูกค้ากรอก</span>,
    },
    {
      icon: faCoins,
      iconClassName: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      label: 'ยอดรับจริง',
      value: <span className="text-blue-600 dark:text-blue-400">฿{fmtAmount(s.total_revenue || 0)}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">รวมสตางค์</span>,
    },
    {
      icon: faCoins,
      iconClassName: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
      label: 'ส่วนต่าง',
      value: (
        <span className="text-amber-600 dark:text-amber-400">
          {(s.cent_difference || 0) > 0 ? '+' : ''}฿{fmtMoney(s.cent_difference || 0)}
        </span>
      ),
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">สตางค์ random</span>,
    },
    {
      icon: faPercent,
      iconClassName: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
      label: 'ค่าธรรมเนียม',
      value: <span className="text-red-600 dark:text-red-400">-฿{fmtMoney(s.total_fee || 0)}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">ค่าบริการรวม</span>,
    },
    {
      icon: faHandHoldingUsd,
      iconClassName: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
      label: 'ยอดสุทธิ',
      value: <span className="text-emerald-600 dark:text-emerald-400">฿{fmtAmount(s.net_revenue || 0)}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">Merchants ได้</span>,
    },
    {
      icon: faChartLine,
      iconClassName: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400',
      label: 'Success Rate',
      value: <span className="text-green-600 dark:text-green-400">{(s.success_rate || 0).toFixed(1)}%</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">อัตราสำเร็จ</span>,
    },
  ], [s]);

  const payoutStatCards: StatCardDef[] = useMemo(() => {
    if (!(s.total_payouts || 0) && !(s.total_payout_fee || 0) && !((s as any).total_p2p_fee || 0)) return [];
    return [
      {
        icon: faMoneyBillWave,
        iconClassName: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400',
        label: 'Payout ทั้งหมด',
        value: <span className="text-indigo-600 dark:text-indigo-400">{(s.total_payouts || 0).toLocaleString()} รายการ</span>,
        subValue: (
          <span className="text-xs text-gray-400 dark:text-slate-500">
            ยอดถอนรวม ฿{fmtAmount(s.total_payout_amount || 0)}
          </span>
        ),
      },
      {
        icon: faPercent,
        iconClassName: 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
        label: 'ค่าธรรมเนียมถอน',
        value: <span className="text-orange-600 dark:text-orange-400">฿{fmtMoney(s.total_payout_fee || 0)}</span>,
        subValue: <span className="text-xs text-gray-400 dark:text-slate-500">Payout Fee รวม</span>,
      },
      {
        icon: faChartPie,
        iconClassName: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
        label: 'Fee รวม (Payin + Payout + P2P)',
        value: (
          <span className="text-purple-600 dark:text-purple-400">
            ฿{fmtMoney((s.total_fee || 0) + (s.total_payout_fee || 0) + ((s as any).total_p2p_fee || 0))}
          </span>
        ),
        subValue: (
          <span className="text-xs text-gray-400 dark:text-slate-500">
            Payin ฿{fmtMoney(s.total_fee || 0)} + Payout ฿{fmtMoney(s.total_payout_fee || 0)} + P2P ฿{fmtMoney((s as any).total_p2p_fee || 0)}
          </span>
        ),
      },
    ];
  }, [s]);

  const columns: Column<MerchantReportRow>[] = useMemo(() => [
    {
      key: 'merchant',
      header: 'Merchant',
      sortable: true,
      render: (m) => (
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 flex-shrink-0">
            <FontAwesomeIcon icon={faBuilding} className="text-xs" />
          </div>
          <div className="min-w-0">
            <p className="font-medium text-gray-800 dark:text-slate-100 text-sm truncate">{m.merchant_name}</p>
            <p className="text-xs text-gray-400 dark:text-slate-500 truncate">{m.merchant_email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'total_orders',
      header: 'Orders',
      sortable: true,
      render: (m) => (
        <span className="font-bold text-gray-800 dark:text-slate-100">{m.total_orders}</span>
      ),
    },
    {
      key: 'paid_orders',
      header: 'Paid',
      sortable: true,
      render: (m) => (
        <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded text-xs font-semibold">
          {m.paid_orders}
        </span>
      ),
    },
    {
      key: 'pending_orders',
      header: 'Pending',
      sortable: true,
      render: (m) => (
        <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded text-xs font-semibold">
          {m.pending_orders}
        </span>
      ),
    },
    {
      key: 'expired_orders',
      header: 'Expired',
      sortable: true,
      render: (m) => (
        <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded text-xs font-semibold">
          {m.expired_orders}
        </span>
      ),
    },
    {
      key: 'base_revenue',
      header: 'ยอดฝาก',
      sortable: true,
      render: (m) => (
        <span className="font-semibold text-teal-600 dark:text-teal-400">
          ฿{fmtAmount(m.base_revenue || 0)}
        </span>
      ),
    },
    {
      key: 'total_revenue',
      header: 'ยอดรับจริง',
      sortable: true,
      render: (m) => (
        <span className="font-semibold text-blue-600 dark:text-blue-400">
          ฿{fmtAmount(m.total_revenue || 0)}
        </span>
      ),
    },
    {
      key: 'cent_difference',
      header: 'ส่วนต่าง',
      sortable: true,
      render: (m) => (
        <span className="font-semibold text-amber-600 dark:text-amber-400">
          {(m.cent_difference || 0) > 0 ? '+' : ''}฿{fmtMoney(m.cent_difference || 0)}
        </span>
      ),
    },
    {
      key: 'total_fee',
      header: 'Fee (P.In)',
      sortable: true,
      render: (m) => (
        <span className="font-semibold text-red-500 dark:text-red-400">
          -฿{fmtMoney(m.total_fee || 0)}
        </span>
      ),
    },
    {
      key: 'total_payout_fee',
      header: 'Fee (P.Out)',
      sortable: true,
      render: (m) => (
        <span className="font-semibold text-orange-500 dark:text-orange-400">
          {(m.total_payout_fee || 0) > 0 ? `฿${fmtMoney(m.total_payout_fee)}` : '-'}
        </span>
      ),
    },
    {
      key: 'p2p_fee',
      header: 'Fee (P2P)',
      sortable: true,
      render: (m) => (
        <span className="font-semibold text-sky-500 dark:text-sky-400">
          {((m as any).p2p_fee || 0) > 0 ? `฿${fmtMoney((m as any).p2p_fee)}` : '-'}
        </span>
      ),
    },
    {
      key: 'net_revenue',
      header: 'ยอดสุทธิ',
      sortable: true,
      render: (m) => (
        <span className="font-bold text-emerald-600 dark:text-emerald-400">
          ฿{fmtAmount(m.net_revenue || 0)}
        </span>
      ),
    },
    {
      key: 'success_rate',
      header: 'Success %',
      sortable: true,
      width: '140px',
      render: (m) => (
        <div className="flex items-center gap-2 min-w-[120px]">
          <div className="flex-1 h-2 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                m.success_rate >= 80 ? 'bg-emerald-500' :
                m.success_rate >= 50 ? 'bg-amber-500' : 'bg-red-500'
              }`}
              style={{ width: `${Math.min(m.success_rate, 100)}%` }}
            />
          </div>
          <span className={`text-xs font-bold min-w-[40px] text-right ${
            m.success_rate >= 80 ? 'text-green-600 dark:text-green-400' :
            m.success_rate >= 50 ? 'text-amber-600 dark:text-amber-400' : 'text-red-500 dark:text-red-400'
          }`}>
            {m.success_rate.toFixed(1)}%
          </span>
        </div>
      ),
    },
  ], []);

  const periodLabel = `${dayjs(appliedDateFrom).format('DD/MM/YYYY')} – ${dayjs(appliedDateTo).format('DD/MM/YYYY')}`;

  return (
    <div className="max-w-full">
      <UiPageHeader
        icon={faChartPie}
        title="System Reports"
        subtitle={`รายงานสรุปของระบบทั้งหมด • ${(reports.merchants || []).length.toLocaleString()} Merchants`}
      />

      <UiFilterPanel
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onSearch={handleSearch}
        onReset={handleReset}
        loading={loading}
        actions={
          <button
            type="button"
            onClick={() => loadReports()}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 hover:bg-gray-50 transition-colors text-gray-600 dark:border-slate-600 dark:hover:bg-slate-700 dark:text-slate-300 flex items-center gap-2 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faRotate} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        }
      />

      <p className="text-xs text-gray-400 dark:text-slate-500 mb-4 -mt-2">
        ช่วงเวลา: {periodLabel}
      </p>

      <UiStatsGrid cards={statCards} loading={loading} cols={4} />

      {payoutStatCards.length > 0 && (
        <UiStatsGrid cards={payoutStatCards} loading={loading} cols={3} className="mt-0" />
      )}

      <P2pReportPanel summary={p2p} />

      <DataTable
        data={reports.merchants || []}
        columns={columns}
        loading={loading}
        keyExtractor={(m) => m.merchant_email || m.merchant_name}
        emptyMessage="ไม่มีข้อมูลในช่วงเวลานี้"
        emptyIcon={<FontAwesomeIcon icon={faChartPie} className="text-5xl text-gray-200 dark:text-slate-600" />}
        pagination={{
          enabled: true,
          pageSize: 10,
          pageSizeOptions: [10, 20, 50],
          showPageSizeSelector: true,
          showInfo: true,
          showFirstLast: true,
        }}
        headerContent={
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={faUsers} className="text-lg text-violet-600 dark:text-violet-400" />
            <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100">รายงานตาม Merchant</h2>
          </div>
        }
        hoverable
        compact
      />
    </div>
  );
};

export default SystemReportsPage;
